import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBookOpen,
  FaClipboardCheck,
  FaCertificate,
  FaVideo,
  FaMedal,
  FaTicketAlt,
  FaKey,
  FaBuilding,
  FaUser,
  FaBell,
  FaStar,
  FaPuzzlePiece
} from 'react-icons/fa';
import './richCards.css';

const ICONS = {
  course: FaBookOpen,
  assessment: FaClipboardCheck,
  certificate: FaCertificate,
  meeting: FaVideo,
  badge: FaMedal,
  ticket: FaTicketAlt,
  license: FaKey,
  organization: FaBuilding,
  profile: FaUser,
  notification: FaBell,
  score: FaStar,
  skill: FaPuzzlePiece
};

const formatMetaDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return String(value);
  }
};

const RichCard = ({ card }) => {
  const navigate = useNavigate();
  const Icon = ICONS[card.type] || FaBookOpen;
  const progress = typeof card.progress === 'number' ? Math.max(0, Math.min(100, card.progress)) : null;

  const handleAction = () => {
    if (!card.action_url) return;
    navigate(card.action_url);
  };

  const handleKeyDown = (event) => {
    if ((event.key === 'Enter' || event.key === ' ') && card.action_url) {
      event.preventDefault();
      handleAction();
    }
  };

  const secondaryLine = card.meta?.meeting_datetime
    ? formatMetaDate(card.meta.meeting_datetime)
    : card.meta?.completion_date
      ? `Completed ${card.meta.completion_date}`
      : card.subtitle;

  return (
    <article
      className={`eg-card eg-card--${card.type}`}
      style={card.color ? { borderColor: card.color } : undefined}
      tabIndex={card.action_url ? 0 : undefined}
      role={card.action_url ? 'link' : 'group'}
      aria-label={`${card.type} card: ${card.title}`}
      onClick={card.action_url ? handleAction : undefined}
      onKeyDown={card.action_url ? handleKeyDown : undefined}
    >
      <div
        className="eg-card__icon"
        style={card.color ? { background: card.color } : undefined}
        aria-hidden="true"
      >
        <Icon />
      </div>
      <div className="eg-card__body">
        <div className="eg-card__top">
          <h4 className="eg-card__title">{card.title}</h4>
          {card.status ? <span className="eg-card__status">{card.status}</span> : null}
        </div>
        {secondaryLine ? <p className="eg-card__subtitle">{secondaryLine}</p> : null}
        {progress !== null ? (
          <div className="eg-card__progress" aria-label={`Progress ${progress}%`}>
            <div className="eg-card__progress-track">
              <div className="eg-card__progress-bar" style={{ width: `${progress}%` }} />
            </div>
            <span className="eg-card__progress-label">{progress}%</span>
          </div>
        ) : null}
        {card.action_label ? (
          <button
            type="button"
            className="eg-card__action"
            onClick={(event) => {
              event.stopPropagation();
              handleAction();
            }}
            aria-label={card.action_label}
          >
            {card.action_label}
          </button>
        ) : null}
      </div>
    </article>
  );
};

const RichCards = ({ cards = [] }) => {
  if (!cards.length) return null;

  return (
    <div className="eg-cards" role="list" aria-label="EduGenie results">
      {cards.map((item) => (
        <div role="listitem" key={`${item.type}-${item.id}`}>
          <RichCard card={item} />
        </div>
      ))}
    </div>
  );
};

export default RichCards;
export { RichCard };
